import { usePage } from '@inertiajs/react';
import type { ReactNode } from 'react';
import { ImpersonationBanner } from '@/components/admin/ImpersonationBanner';
import { HackerSidebar } from '@/components/hacker/HackerSidebar';
import { cn } from '@/lib/utils';

// The persistent shell of every hacker portal page — the port of the four
// src/components/hacker/protected-layout*.tsx twins as one component (PORTING.md G5).
// Everything that differed between them is decided server-side by HackerPortal and
// arrives on the `hackerPortal` shared prop: the palette, whether the sidebar survives
// the onboarding pages, and which edition's sidebar this is.
//
// It takes only `children` — an Inertia layout function is called twice and the first
// call gets the raw page props, so anything it destructures from an argument blows up.
export function HackerPortalLayout({ children }: { children: ReactNode }) {
  const { hackerPortal, auth } = usePage().props;

  if (!hackerPortal) {
    return <>{children}</>;
  }

  return (
    <div
      className={cn(
        'flex h-screen flex-col overflow-hidden',
        hackerPortal.theme26 &&
          'theme-ph26 selection-on-light bg-background text-foreground',
      )}
    >
      <ImpersonationBanner
        impersonatedBy={hackerPortal.impersonatedBy}
        userName={auth.user?.name ?? null}
        userEmail={auth.user?.email ?? null}
      />
      <div className="flex flex-1 overflow-hidden">
        {hackerPortal.showSidebar ? (
          <div className="hidden w-64 md:block">
            <HackerSidebar
              edition={hackerPortal.edition}
              hacker={hackerPortal.hacker}
              discordGuildId={hackerPortal.discordGuildId}
              discordFaqChannelId={hackerPortal.discordFaqChannelId}
              showMusic={hackerPortal.showMusic}
            />
          </div>
        ) : null}
        <main className="flex-1 overflow-y-auto bg-background">{children}</main>
      </div>
    </div>
  );
}
